// 1Rotary — Enhanced Operational Digital Twin
// Drill-down datasets, AI capabilities and scenario library

let _s = 77_004_201;
function rnd() { _s = (_s * 1664525 + 1013904223) % 4294967296; return _s / 4294967296; }
function r(a, b) { return a + rnd() * (b - a); }
function ri(a, b) { return Math.floor(r(a, b + 1)); }

/* =========================================================
   L1 — Topology drill-down details per node
========================================================= */
export const TOPOLOGY_DRILLDOWN = {
  HQ: {
    title: "1Rotary HQ (Asterisk Tower, BGC)",
    metrics: { reports: 4, headcount: 1_034, ytdRevenue: 1_842_000_000 },
    aiRouting: { confidence: 0.94, alertsRouted: 2_184, decisionsLogged: 412 },
    recentDecisions: [
      "Approved Q2 import budget — ₱128M (Feb 14)",
      "Greenlit NPI-2026-001 EV-AC Compressor sourcing (Feb 12)",
      "Capella supplier renegotiation accept Net-75 terms (Feb 10)",
    ],
    keyPolicies: ["Treasury min-cash ₱45M", "Branch CapEx ceiling ₱8M/yr", "Cross-region transfer auto-approve <₱500K"],
  },
  VP: {
    title: "VP Operations (Reports to CEO)",
    metrics: { reports: 5, branches: 86, headcount: 894 },
    aiRouting: { confidence: 0.91, alertsRouted: 842, decisionsLogged: 168 },
    recentDecisions: [
      "Escalated Cotabato underperformance to RM-MIN (Feb 16)",
      "Approved 14u air-freight Honda CR-V Aux Fan (Feb 15)",
    ],
    keyPolicies: ["Region OpEx ±5% guardrail", "Branch closure requires 3-yr financial review"],
  },
  DP: {
    title: "Demand Planning",
    metrics: { skusForecasted: 1_840, forecastAccuracy: 0.782, modelsRunning: 14 },
    aiRouting: { confidence: 0.88, alertsRouted: 624, decisionsLogged: 86 },
    recentDecisions: ["Revised Q2 Auto forecast +18% (Feb 14)", "Heat-index ML model v3.2 deployed (Feb 11)"],
    keyPolicies: ["Forecast horizon 90 days A-class · 365 days planning", "A-class accuracy target 80%+"],
  },
  MM: {
    title: "Merchandising",
    metrics: { activeSkus: 8_240, lifecycleActive: 6_180, dailyDecisions: 142 },
    aiRouting: { confidence: 0.86, alertsRouted: 318, decisionsLogged: 64 },
    recentDecisions: ["Phased out Foam Filter Roll Eco (Feb 13)", "Approved Shopee 4.4 promo plan (Feb 12)"],
    keyPolicies: ["Markup band 18–42% by ABC class", "Phase-out trigger: 180 days idle + decline stage"],
  },
  MO: {
    title: "Marketing Operations",
    metrics: { campaignsLive: 12, shopeeBudget: 4_800_000, ctr: 0.084 },
    aiRouting: { confidence: 0.79, alertsRouted: 184, decisionsLogged: 48 },
    recentDecisions: ["Reallocated ₱1.4M from CDMO 12.12 to NCR pre-summer", "EV Compressor search-trend escalated"],
    keyPolicies: ["Promo cannibalization <20%", "Shopee weekly creative refresh"],
  },
  "RM-NCR": {
    title: "Regional Manager — Metro Manila",
    metrics: { branches: 25, mtdRevenue: 122_400_000, achievement: 1.04 },
    aiRouting: { confidence: 0.92, alertsRouted: 442, decisionsLogged: 88 },
    recentDecisions: ["Rotated 60u from Cebu to Mandaue (Feb 16)", "Approved Quezon Cubao staff +1 FTE"],
    keyPolicies: ["Branch ach < 85% triggers monthly review", "Cluster transfer auto-approve <₱100K"],
  },
  "RM-SL": { title: "RM — South Luzon", metrics: { branches: 25, mtdRevenue: 88_400_000, achievement: 0.96 }, aiRouting: { confidence: 0.84, alertsRouted: 312, decisionsLogged: 62 }, recentDecisions: ["Calamba shelf re-mix approved"], keyPolicies: ["Standard"] },
  "RM-NL": { title: "RM — North Luzon", metrics: { branches: 25, mtdRevenue: 64_800_000, achievement: 0.94 }, aiRouting: { confidence: 0.81, alertsRouted: 268, decisionsLogged: 54 }, recentDecisions: ["Cabanatuan East stock transfer"], keyPolicies: ["Standard"] },
  "RM-VIS": { title: "RM — Visayas", metrics: { branches: 9, mtdRevenue: 48_200_000, achievement: 1.08 }, aiRouting: { confidence: 0.86, alertsRouted: 142, decisionsLogged: 38 }, recentDecisions: ["Cebu cluster shelf-space refresh"], keyPolicies: ["Standard"] },
  "RM-MIN": { title: "RM — Mindanao", metrics: { branches: 8, mtdRevenue: 28_400_000, achievement: 0.92 }, aiRouting: { confidence: 0.78, alertsRouted: 112, decisionsLogged: 28 }, recentDecisions: ["Cotabato action plan requested"], keyPolicies: ["Standard"] },
};

/* =========================================================
   L2 — Policy & Rules effectiveness + audit + AI suggestions
========================================================= */
export const RULE_EFFECTIVENESS = [
  { code: "R1", name: "Minimum Order Quantity", lastChanged: "2024-08-12", triggered: 2_184, overridden: 142, savings: 4_200_000, aiSuggestion: "Reduce Sanden MOQ from 50→40 units to align with revised demand", confidence: 0.84 },
  { code: "R2", name: "Supplier Payment Terms", lastChanged: "2025-01-04", triggered: 318, overridden: 28, savings: 8_400_000, aiSuggestion: "Tighten to Net-30 for local suppliers with OTD > 95%", confidence: 0.91 },
  { code: "R3", name: "Markup Policy", lastChanged: "2023-11-22", triggered: 12_840, overridden: 4_280, savings: 18_400_000, aiSuggestion: "Raise A-class band to 20-26% (elasticity model says inelastic on top 80 SKUs)", confidence: 0.88 },
  { code: "R4", name: "Branch Credit Limit", lastChanged: "2024-03-18", triggered: 642, overridden: 84, savings: 2_400_000, aiSuggestion: "Increase Tier-1 to ₱750K for accounts with 36+ month tenure", confidence: 0.79 },
  { code: "R5", name: "Reorder Policy", lastChanged: "2025-09-08", triggered: 5_840, overridden: 612, savings: 6_800_000, aiSuggestion: "Add weather-index multiplier for Auto/HVAC categories (R²=0.78)", confidence: 0.92 },
  { code: "R6", name: "Promotion Cadence", lastChanged: "2025-11-01", triggered: 168, overridden: 12, savings: 14_200_000, aiSuggestion: "Add micro-promo every 2nd Fri to smooth Shopee mega-event cannibalization", confidence: 0.74 },
];

export const RULE_AUDIT_LOG = [
  { ts: "2026-02-15 09:42", code: "R2", action: "AI-suggested change", actor: "AI Copilot", detail: "Proposed Net-30 tightening for local suppliers OTD>95% → CFO review pending" },
  { ts: "2026-02-12 14:18", code: "R5", action: "Modified", actor: "demand-planning@1rotary", detail: "Lead-time multiplier increased Capella 1.0→1.15 (delay pattern)" },
  { ts: "2026-02-08 11:05", code: "R3", action: "Override logged", actor: "merch-mgr@1rotary", detail: "Sanden compressor B-class markup +6pp for ₱1.4M deal" },
  { ts: "2026-02-04 08:30", code: "R1", action: "AI-suggested change", actor: "AI Copilot", detail: "MOQ reduction recommended — backlog reduced to 12 SKUs" },
  { ts: "2026-01-22 16:50", code: "R6", action: "Activated", actor: "marketing-ops@1rotary", detail: "Q1 2026 Shopee anchor event calendar locked" },
];

/* =========================================================
   L3 — Data stream catalog with quality scores
========================================================= */
export const DATA_STREAMS = [
  { id: "STR-POS", name: "POS Transactions", source: "Branch ERP", freq: "real-time (5s)", rowsPerDay: 184_200, latencySec: 8, completeness: 0.998, freshness: 0.96, anomalyRate: 0.012, qualityScore: 0.96, layer: "Bronze" },
  { id: "STR-INV", name: "Branch Inventory Snapshot", source: "Branch ERP", freq: "every 5 min", rowsPerDay: 612_840, latencySec: 22, completeness: 0.994, freshness: 0.92, anomalyRate: 0.018, qualityScore: 0.94, layer: "Bronze" },
  { id: "STR-PO", name: "Purchase Orders & Receipts", source: "HQ Procurement", freq: "every 30 min", rowsPerDay: 412, latencySec: 1_840, completeness: 1.0, freshness: 0.88, anomalyRate: 0.004, qualityScore: 0.97, layer: "Silver" },
  { id: "STR-SHOPEE", name: "Shopee Orders & Returns", source: "Shopee Seller API", freq: "every 2 min", rowsPerDay: 8_420, latencySec: 142, completeness: 0.984, freshness: 0.84, anomalyRate: 0.024, qualityScore: 0.91, layer: "Bronze" },
  { id: "STR-DIRECT", name: "Direct B2B Orders", source: "Salesforce CRM", freq: "hourly", rowsPerDay: 184, latencySec: 3_400, completeness: 0.992, freshness: 0.72, anomalyRate: 0.006, qualityScore: 0.92, layer: "Silver" },
  { id: "STR-FIN", name: "Financial Close (GL)", source: "Oracle Fusion", freq: "daily", rowsPerDay: 24_800, latencySec: 86_400, completeness: 0.998, freshness: 1.0, anomalyRate: 0.002, qualityScore: 0.97, layer: "Gold" },
  { id: "STR-HR", name: "HR & Payroll", source: "Workday", freq: "daily", rowsPerDay: 4_240, latencySec: 86_400, completeness: 1.0, freshness: 1.0, anomalyRate: 0.001, qualityScore: 0.99, layer: "Gold" },
  { id: "STR-WX", name: "PAGASA Weather Feed", source: "PAGASA API", freq: "every 15 min", rowsPerDay: 24_000, latencySec: 28, completeness: 0.96, freshness: 0.94, anomalyRate: 0.082, qualityScore: 0.87, layer: "Bronze" },
];

export const LAKEHOUSE_STATS = {
  bronze: { volumeGb: 184.2, tables: 42, partitions: 8_240 },
  silver: { volumeGb: 86.4, tables: 28, partitions: 4_120 },
  gold: { volumeGb: 14.8, tables: 18, partitions: 1_240 },
  pipelineHealth: 0.94,
  jobsLastDay: 312,
  failedJobs: 4,
};

/* =========================================================
   L4 — Process mining: cycle times + bottlenecks per step
========================================================= */
export const PROCESS_MINING = [
  {
    name: "Branch Replenishment",
    cycleTimeHrsActual: 38.4, cycleTimeHrsDesigned: 24, deviation: 0.60,
    steps: [
      { name: "Demand Signal", hrs: 0.4, designed: 0.2, status: "ok" },
      { name: "AI Reorder Calc", hrs: 0.2, designed: 0.2, status: "ok" },
      { name: "Approval", hrs: 18.2, designed: 8, status: "bottleneck" },
      { name: "Transfer/PO", hrs: 14.8, designed: 12, status: "warning" },
      { name: "Receipt", hrs: 4.8, designed: 3.6, status: "ok" },
    ],
    aiInsight: "Approval queue is the primary bottleneck (2.3× designed) — recommend auto-approve threshold raise from ₱100K to ₱250K for transfers within same region.",
    aiSavingsIfFixed: 3_200_000,
  },
  {
    name: "Import Procurement",
    cycleTimeHrsActual: 1_944, cycleTimeHrsDesigned: 1_620, deviation: 0.20,
    steps: [
      { name: "Forecast Roll-up", hrs: 24, designed: 24, status: "ok" },
      { name: "Supplier RFQ", hrs: 168, designed: 120, status: "warning" },
      { name: "PO Issue", hrs: 48, designed: 24, status: "warning" },
      { name: "LC Setup", hrs: 96, designed: 72, status: "warning" },
      { name: "Port Clearance", hrs: 1_344, designed: 1_080, status: "bottleneck" },
      { name: "DC Receipt", hrs: 264, designed: 300, status: "ok" },
    ],
    aiInsight: "Port Clearance variance driven by congestion at Manila South Harbor (Jan-Feb peak). AI recommends booking pre-cleared customs broker partner — historical 3-4 day reduction.",
    aiSavingsIfFixed: 4_800_000,
  },
  {
    name: "Omnichannel Fulfillment",
    cycleTimeHrsActual: 4.2, cycleTimeHrsDesigned: 3.5, deviation: 0.20,
    steps: [
      { name: "Order", hrs: 0.2, designed: 0.1, status: "ok" },
      { name: "Allocate", hrs: 0.6, designed: 0.4, status: "ok" },
      { name: "Pick", hrs: 1.4, designed: 1.2, status: "warning" },
      { name: "Ship", hrs: 1.8, designed: 1.6, status: "ok" },
      { name: "Confirm", hrs: 0.2, designed: 0.2, status: "ok" },
    ],
    aiInsight: "Pick step pressure during 6-8pm Shopee peak. Recommend dynamic pick-staging at Pasig DC during Shopee anchor events.",
    aiSavingsIfFixed: 1_240_000,
  },
  {
    name: "New SKU Introduction",
    cycleTimeHrsActual: 2_160, cycleTimeHrsDesigned: 1_440, deviation: 0.50,
    steps: [
      { name: "Demand Brief", hrs: 96, designed: 72, status: "ok" },
      { name: "Supplier Source", hrs: 720, designed: 480, status: "bottleneck" },
      { name: "Pilot Branches", hrs: 480, designed: 336, status: "warning" },
      { name: "Scale", hrs: 720, designed: 408, status: "bottleneck" },
      { name: "Review", hrs: 144, designed: 144, status: "ok" },
    ],
    aiInsight: "Supplier sourcing for novel SKUs (e.g. EV-AC) suffers 10-day RFQ lag. AI Sourcing Agent (NPI-2026-002) can shortlist suppliers from CSE/Alibaba in 36h.",
    aiSavingsIfFixed: 6_400_000,
  },
];

/* =========================================================
   L5 — External feed signal strength & correlation
========================================================= */
export const FEED_SIGNALS = [
  { name: "PAGASA Heat Index", correl: 0.81, leadDays: 14, sensitivity: "HVAC + Auto-AC demand × 1.4 per +3°C", confidence: 0.92 },
  { name: "PAGASA Typhoon Outlook", correl: 0.42, leadDays: 7, sensitivity: "Cold-chain refrigerant +24% peak; logistics -18% during landfall", confidence: 0.78 },
  { name: "LTO Vehicle Registrations", correl: 0.68, leadDays: 90, sensitivity: "Auto-AC consumables +0.4% per 10K reg", confidence: 0.84 },
  { name: "Copper Spot (LME)", correl: 0.34, leadDays: 30, sensitivity: "COGS +1.1% per +10% copper (4-6 wk lag)", confidence: 0.72 },
  { name: "PHP/USD FX", correl: 0.48, leadDays: 14, sensitivity: "Import COGS +0.6% per +1₱ devaluation", confidence: 0.86 },
  { name: "Shopee Take Rate", correl: 0.62, leadDays: 1, sensitivity: "Shopee GM -0.5pp per +0.5pp take-rate", confidence: 0.95 },
];

/* =========================================================
   AI SCENARIO LIBRARY — 10+ scenarios with parameters
========================================================= */
export const SCENARIO_LIBRARY = [
  {
    id: "SCN-001", icon: "Flame", category: "Climate", urgency: "high",
    name: "Heatwave Surge — NCR +5°C for 21 days",
    triggerSignal: "PAGASA heat-advisory tier 3",
    parameters: [
      { name: "Heat Index Δ", value: "+5°C", baseline: "+0°C", impact: "x1.8 HVAC demand" },
      { name: "Duration", value: "21 days", baseline: "—", impact: "Mar 22 - Apr 12" },
      { name: "Affected Branches", value: "NCR 25", baseline: "all", impact: "+₱32M revenue opportunity" },
    ],
    expectedOutcomes: { revenue: 32_400_000, stockoutRisk: 0.38, marginImpact: -0.012, branches: 25 },
    aiActions: ["Pre-position 1,840u inverter compressors", "Air-freight Freon R32 from Capella", "Shopee NCR-targeted ad spend +₱680K"],
    confidence: 0.84,
  },
  {
    id: "SCN-002", icon: "CloudRain", category: "Climate", urgency: "high",
    name: "Super Typhoon Landfall — E. Visayas",
    triggerSignal: "PAGASA Cat-4 forecast 72h out",
    parameters: [
      { name: "Wind Speed", value: "Cat-4 / 220 km/h", baseline: "—", impact: "8 branches offline 4 days" },
      { name: "Landfall Window", value: "Mar 8-10", baseline: "—", impact: "₱14.2M revenue at risk" },
      { name: "Cold-Chain Disruption", value: "48-72h power loss", baseline: "—", impact: "+24% refrigerant demand spike" },
    ],
    expectedOutcomes: { revenue: -8_200_000, stockoutRisk: 0.62, marginImpact: 0.018, branches: 8 },
    aiActions: ["Suspend Visayas outbound shipments", "Surge refrigerant to Cebu DC", "Insurance claim pre-staging"],
    confidence: 0.78,
  },
  {
    id: "SCN-003", icon: "DollarSign", category: "Macro", urgency: "medium",
    name: "Peso Devaluation — ₱62/$",
    triggerSignal: "FX rate breach ₱60",
    parameters: [
      { name: "PHP/USD", value: "₱62.00", baseline: "₱58.40", impact: "+6.2% import COGS" },
      { name: "Affected SKUs", value: "1,240 imports", baseline: "—", impact: "₱42M annualized" },
      { name: "Pass-through Window", value: "60 days", baseline: "—", impact: "Margin compression -2.4pp" },
    ],
    expectedOutcomes: { revenue: 0, stockoutRisk: 0.0, marginImpact: -0.024, branches: 86 },
    aiActions: ["Renegotiate top-5 supplier USD terms", "Selective price-pass-through on inelastic SKUs", "FX hedge ₱180M Q2 exposure"],
    confidence: 0.89,
  },
  {
    id: "SCN-004", icon: "Ship", category: "Supply Chain", urgency: "high",
    name: "Manila Port Strike — 14 days",
    triggerSignal: "Union strike notice issued",
    parameters: [
      { name: "Strike Duration", value: "14 days", baseline: "—", impact: "12 shipments delayed avg 18d" },
      { name: "POs Affected", value: "8 active", baseline: "—", impact: "₱48M inventory in-transit blocked" },
      { name: "Air-Freight Premium", value: "+₱18/kg", baseline: "—", impact: "+₱2.4M emergency cost" },
    ],
    expectedOutcomes: { revenue: -12_400_000, stockoutRisk: 0.71, marginImpact: -0.018, branches: 64 },
    aiActions: ["Route 4 priority POs through Subic", "Air-freight A-class SKUs", "Communicate to top-20 B2B accounts"],
    confidence: 0.81,
  },
  {
    id: "SCN-005", icon: "Store", category: "Competitive", urgency: "medium",
    name: "Mega-Retailer Enters NCR (Suki Auto)",
    triggerSignal: "Competitor lease signing detected",
    parameters: [
      { name: "Competitor Stores", value: "8 new in NCR", baseline: "0", impact: "Footfall -8% Yr 1" },
      { name: "Price Index", value: "0.94 (6% cheaper)", baseline: "1.00", impact: "Margin pressure on overlap SKUs" },
      { name: "Overlap SKUs", value: "1,840 (22% of cat.)", baseline: "—", impact: "₱18M revenue at risk" },
    ],
    expectedOutcomes: { revenue: -14_800_000, stockoutRisk: 0.04, marginImpact: -0.014, branches: 25 },
    aiActions: ["Lock-in Pro Mechanics loyalty program", "Selective price-match on overlap A-class", "Tier-1 corporate-account stickiness program"],
    confidence: 0.72,
  },
  {
    id: "SCN-006", icon: "ShoppingCart", category: "Channel", urgency: "low",
    name: "Shopee Algorithm Change — Listing Quality",
    triggerSignal: "Shopee Seller News bulletin",
    parameters: [
      { name: "Listing Quality Score Δ", value: "-0.18 avg", baseline: "0.82", impact: "Search visibility -22%" },
      { name: "Affected Listings", value: "624 SKUs", baseline: "—", impact: "₱4.8M monthly revenue" },
      { name: "Recovery Window", value: "30 days", baseline: "—", impact: "Creative + price + reviews refresh" },
    ],
    expectedOutcomes: { revenue: -2_800_000, stockoutRisk: 0.02, marginImpact: 0.004, branches: 0 },
    aiActions: ["AI listing optimizer on 624 SKUs", "Refresh hero images for top-200", "Boost ad spend on conversion-positive SKUs"],
    confidence: 0.86,
  },
  {
    id: "SCN-007", icon: "Fuel", category: "Cost", urgency: "medium",
    name: "Fuel Price Spike — Diesel +30%",
    triggerSignal: "Geopolitical event + OPEC announcement",
    parameters: [
      { name: "Diesel Price Δ", value: "+30%", baseline: "₱64.20/L", impact: "Last-mile cost +₱840K/mo" },
      { name: "Affected Routes", value: "All 24 routes", baseline: "—", impact: "Annual ₱10.1M OpEx" },
      { name: "Pass-through to Customer", value: "Surcharge ₱120/order", baseline: "Free delivery", impact: "Volume risk -4%" },
    ],
    expectedOutcomes: { revenue: -3_400_000, stockoutRisk: 0.02, marginImpact: -0.008, branches: 86 },
    aiActions: ["Re-optimize routes (-12% km)", "Selective B2B fuel surcharge", "Accelerate EV-van pilot in Pasig hub"],
    confidence: 0.83,
  },
  {
    id: "SCN-008", icon: "Zap", category: "Demand", urgency: "low",
    name: "EV Adoption Tipping Point",
    triggerSignal: "LTO EV registrations cross 10K/mo",
    parameters: [
      { name: "EV Registrations", value: "12,400/mo", baseline: "3,200/mo", impact: "+₱28M/yr EV-AC opportunity" },
      { name: "EV-AC SKU Pipeline", value: "0 in catalog", baseline: "—", impact: "NPI-2026-002 fast-track" },
      { name: "Service Network Gap", value: "32 branches need cert.", baseline: "0", impact: "Training ₱680K" },
    ],
    expectedOutcomes: { revenue: 28_400_000, stockoutRisk: 0.0, marginImpact: 0.022, branches: 32 },
    aiActions: ["Accelerate Sanden EV compressor sourcing", "Train 32 branches on R290/R1234yf refrigerants", "Shopee category page for EV-AC"],
    confidence: 0.76,
  },
  {
    id: "SCN-009", icon: "Building2", category: "Network", urgency: "medium",
    name: "Branch Closure — Cotabato (3y underperform)",
    triggerSignal: "3-yr achievement <85% & negative trend",
    parameters: [
      { name: "Branch", value: "Cotabato Mindanao", baseline: "—", impact: "₱18M annual revenue lost" },
      { name: "Recoverable Inventory", value: "₱8.4M", baseline: "—", impact: "Transfer to 4 nearby branches" },
      { name: "Annual OpEx Saved", value: "₱14.8M", baseline: "—", impact: "Net positive ₱2.8M" },
    ],
    expectedOutcomes: { revenue: -8_400_000, stockoutRisk: 0.04, marginImpact: 0.014, branches: -1 },
    aiActions: ["Inventory rebalance manifest auto-generated", "Customer migration plan to GenSan / Davao", "Stakeholder communication 60d notice"],
    confidence: 0.81,
  },
  {
    id: "SCN-010", icon: "Sparkles", category: "Product", urgency: "low",
    name: "AI Stock Advisor Network-Wide Rollout",
    triggerSignal: "Pilot complete with positive ROI",
    parameters: [
      { name: "Branches Onboarded", value: "86", baseline: "12 (pilot)", impact: "Daily action queue × network" },
      { name: "Forecast Accuracy Lift", value: "+8pp", baseline: "78.2%", impact: "Stockout reduction -2.1pp" },
      { name: "Working Capital Unlock", value: "₱48M", baseline: "—", impact: "Annualized" },
    ],
    expectedOutcomes: { revenue: 24_000_000, stockoutRisk: -0.021, marginImpact: 0.018, branches: 86 },
    aiActions: ["Branch-manager training waves (Q2-Q3)", "Pilot-to-prod model refresh", "Quarterly ROI dashboard"],
    confidence: 0.88,
  },
  {
    id: "SCN-011", icon: "Snowflake", category: "Regulation", urgency: "medium",
    name: "Cold-Chain Regulatory Mandate (FDA)",
    triggerSignal: "FDA circular CDR-2026-04 published",
    parameters: [
      { name: "Compliance Window", value: "180 days", baseline: "—", impact: "All refrig units IoT-monitored" },
      { name: "Affected Customers", value: "312 cold-chain", baseline: "—", impact: "Service contracts revisited" },
      { name: "Capital Required", value: "₱6.4M (IoT)", baseline: "—", impact: "1-yr payback at 18% margin" },
    ],
    expectedOutcomes: { revenue: 18_000_000, stockoutRisk: 0.02, marginImpact: 0.012, branches: 0 },
    aiActions: ["Partner with IoT vendor (Bevy/Sensata)", "Customer compliance webinar series", "New 'Monitoring-as-a-Service' SKU"],
    confidence: 0.74,
  },
  {
    id: "SCN-012", icon: "TrendingDown", category: "Macro", urgency: "high",
    name: "Recession / Pandemic-style Demand Collapse",
    triggerSignal: "GDP forecast revision <0% + COVID-style mobility drop",
    parameters: [
      { name: "Demand Drop", value: "-38% peak", baseline: "—", impact: "₱62M monthly revenue loss" },
      { name: "Recovery Window", value: "6-9 months", baseline: "—", impact: "Stair-step recovery" },
      { name: "Working Capital Stress", value: "Cash days 22→9", baseline: "—", impact: "Critical — revolving credit + cost cuts" },
    ],
    expectedOutcomes: { revenue: -62_000_000, stockoutRisk: -0.18, marginImpact: -0.034, branches: 86 },
    aiActions: ["Cancel/defer 60% of Q2 POs", "Hiring freeze + voluntary leave", "Pivot Shopee to essentials + repair parts", "Negotiate rent deferrals top-20 sites"],
    confidence: 0.68,
  },
];

export const SCENARIO_CATEGORIES = [
  { id: "all", name: "All Scenarios" },
  { id: "Climate", name: "Climate" },
  { id: "Macro", name: "Macro / FX" },
  { id: "Supply Chain", name: "Supply Chain" },
  { id: "Competitive", name: "Competitive" },
  { id: "Channel", name: "Channel" },
  { id: "Cost", name: "Cost" },
  { id: "Demand", name: "Demand" },
  { id: "Network", name: "Network" },
  { id: "Product", name: "Product" },
  { id: "Regulation", name: "Regulation" },
];
